import dayjs from '~/timeInit';

import { SYDNEY_TIMEZONE, formatDateWithClock } from '~/services/TimeService';

const dateFormatter = (date) => {
  if (date) {
    return formatDateWithClock(date);
  }

  return '';
};

export default dateFormatter;

/**
 * Ensures a date string has an ISO 8601 time component.
 * Bare `YYYY-MM-DD` values are interpreted as start-of-day in Sydney time (so a picked
 * calendar day means midnight AEST/AEDT, not UTC midnight). Values that already contain
 * a time component pass through unchanged.
 */
export function normalizeToISODate(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.includes('T') ? trimmed : dayjs.tz(trimmed, 'YYYY-MM-DD', SYDNEY_TIMEZONE).toISOString();
}

/**
 * `datetime-local` (`YYYY-MM-DDTHH:mm`) interpreted in the browser's local timezone → ISO instant;
 * bare `YYYY-MM-DD` via `normalizeToISODate`. Voucher forms use
 * `normalizeSydneyFormDateStringToIso` instead so entered times mean Sydney wall-clock time.
 */
export function normalizeFormDateStringToIso(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) {
    return dayjs(trimmed).toISOString();
  }
  return normalizeToISODate(trimmed);
}

/**
 * Voucher-form variant: `datetime-local` (`YYYY-MM-DDTHH:mm`) interpreted as Sydney wall-clock
 * time; full ISO instants pass through; bare `YYYY-MM-DD` → Sydney start-of-day.
 */
export function normalizeSydneyFormDateStringToIso(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(trimmed)) {
    return dayjs.tz(trimmed, 'YYYY-MM-DDTHH:mm', SYDNEY_TIMEZONE).toISOString();
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) {
    return dayjs(trimmed).toISOString();
  }
  return normalizeToISODate(trimmed);
}

/**
 * ISO instant → `YYYY-MM-DD` in Sydney time, for prefilling date-only form inputs.
 * Bare `YYYY-MM-DD` values are returned as-is.
 */
export function isoToSydneyDate(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!trimmed.includes('T')) return trimmed;
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed.tz(SYDNEY_TIMEZONE).format('YYYY-MM-DD') : '';
}

/**
 * Default date (YYYY-MM-DD) for "add voucher users" expiry from voucher settings:
 * today + `voucher_user_expires_in_days`, else fixed `voucher_user_expires_at`, else +1 year.
 */
export function getDefaultAddVoucherUsersExpiryDate(params: {
  voucherUserExpiresAt: string;
  voucherUserExpiresInDays: string;
}): string {
  const daysTrimmed = params.voucherUserExpiresInDays.trim();
  if (daysTrimmed !== '') {
    const parsed = Number.parseInt(daysTrimmed, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return dayjs().tz(SYDNEY_TIMEZONE).add(parsed, 'day').format('YYYY-MM-DD');
    }
  }
  const at = params.voucherUserExpiresAt.trim();
  if (at) {
    return isoToSydneyDate(at);
  }
  return dayjs().tz(SYDNEY_TIMEZONE).add(1, 'year').format('YYYY-MM-DD');
}
